/**
 * Ticket Card Component
 *
 * Displays a support ticket summary in a card format.
 * Used in ticket lists, dashboards, and search results.
 */

import React from 'react';
import { Clock, User, MessageSquare } from 'lucide-react';
import { Card, CardBody } from '../common/Card';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketPriorityBadge } from './TicketPriorityBadge';
import { TicketTypeBadge } from './TicketTypeBadge';
import type { SupportTicket } from '../../types/support';
import { formatDate } from '../../utils/formatting';

export interface TicketCardProps {
  /** Ticket data to display */
  ticket: SupportTicket;

  /** Click handler for the card */
  onClick?: () => void;

  /** Show company name (for admin/support view) */
  showCompany?: boolean;

  /** Compact mode (hide description) */
  compact?: boolean;

  /** Custom className */
  className?: string;
}

export const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  onClick,
  showCompany = false,
  compact = false,
  className = '',
}) => {
  return (
    <Card
      hover
      onClick={onClick}
      className={`cursor-pointer transition-all duration-200 ${className}`}
    >
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Ticket Number & Badges */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-mono text-neutral-600">{ticket.ticketNumber}</span>
              <TicketStatusBadge status={ticket.status} size="sm" />
              <TicketPriorityBadge priority={ticket.priority} size="sm" />
            </div>

            {/* Subject */}
            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{ticket.subject}</h3>

            {/* Company Name (admin view) */}
            {showCompany && (
              <p className="text-sm text-neutral-600 mb-2">{ticket.companyName}</p>
            )}

            {/* Description preview (non-compact) */}
            {!compact && ticket.description && (
              <p className="text-sm text-neutral-700 line-clamp-2 mb-3">{ticket.description}</p>
            )}

            {/* Meta info */}
            <div className="flex items-center gap-4 text-xs text-neutral-600 flex-wrap">
              {/* Type */}
              <div className="flex items-center gap-1">
                <TicketTypeBadge type={ticket.type} size="sm" />
              </div>

              {/* Created date */}
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{formatDate(ticket.createdAt)}</span>
              </div>

              {/* Assigned to */}
              {ticket.assignedToName && (
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>{ticket.assignedToName}</span>
                </div>
              )}

              {/* Message count */}
              {ticket.messageCount > 0 && (
                <div className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  <span>{ticket.messageCount}</span>
                </div>
              )}
            </div>
          </div>

          {/* SLA deadline indicator (if approaching or past due) */}
          {ticket.slaDeadline && (
            <div className="flex-shrink-0">
              <SLAIndicator deadline={ticket.slaDeadline} />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

/**
 * SLA Deadline Indicator
 *
 * Shows visual warning if SLA deadline is approaching or past due.
 */
const SLAIndicator: React.FC<{ deadline: Date }> = ({ deadline }) => {
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const hoursUntilDeadline = (deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60);

  // Past due
  if (hoursUntilDeadline < 0) {
    return (
      <div className="px-2 py-1 rounded-md bg-error-100 text-error-700 text-xs font-medium">
        Overdue
      </div>
    );
  }

  // Less than 2 hours
  if (hoursUntilDeadline < 2) {
    return (
      <div className="px-2 py-1 rounded-md bg-error-100 text-error-700 text-xs font-medium">
        {Math.floor(hoursUntilDeadline * 60)}m
      </div>
    );
  }

  // Less than 24 hours
  if (hoursUntilDeadline < 24) {
    return (
      <div className="px-2 py-1 rounded-md bg-warning-100 text-warning-700 text-xs font-medium">
        {Math.floor(hoursUntilDeadline)}h
      </div>
    );
  }

  // No indicator needed
  return null;
};
